/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { FormEvent, KeyboardEvent } from "react";
import type {
  OnFormChange,
  FormStates,
  FormValues,
} from '@shared/types';
import { z } from 'zod';
import { Provider } from "./form-store/hooks";
import {
  focusNextField,
  handleExpandedCombobox,
  shouldIgnoreKeyDown,
} from "./form-store/focus-navigation";

export {
  useFormStore,
  useFormStoreFieldData,
  useFormStoreFieldValue,
  useFormStoreFieldError,
  useFormStoreFieldState,
  useFormStoreFieldValidation,
  useFormStoreSetters,
  FormConsumer,
} from "./form-store/hooks";

export type FormProviderProps = {
  initialValues?: FormValues;
  initialStates?: FormStates;
  onChangeValues?: OnFormChange;
  required?: string[];
  schema?: z.ZodSchema;
};

const FormProvider: ComponentWithChildren<FormProviderProps> = ({
  initialValues,
  initialStates,
  onChangeValues,
  required,
  schema,
  onSubmit,
  children,
}) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (shouldIgnoreKeyDown(event)) return;

    const target = event.target as HTMLElement;
    const form = event.currentTarget as HTMLElement | null;
    if (!form) return;

    if (handleExpandedCombobox(form, target)) return;

    event.preventDefault();
    focusNextField(form, target);
  };

  return (
    <form
      style={{ display: "contents" }}
      onSubmit={(e: FormEvent<HTMLFormElement>) => e.preventDefault()}
      onKeyDown={handleKeyDown}
    >
      <Provider
        initialState={{
          formValues: initialValues,
          formStates: initialStates,
          _onChangeValues: onChangeValues,
          _onSubmit: onSubmit,
          required,
          schema: schema,
        }}
      >
        {children}
      </Provider>
    </form>
  );
};

export default FormProvider;
